import React, { useState, useMemo, useEffect, useCallback } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { mergeSelectStyles } from "../../../shared/darkSelectStyles";
import { formatCurrency } from "../utils/formatCurrency";
import ScenarioTable from "./ScenarioTable";
import DealInputs from "./DealInputs";
import SummaryCard from "./SummaryCard";
import Dropdown from "./Dropdown";

const axios = require('axios').default;

const DEAL_TYPES = [
  { id: "versus", name: "Guarantee vs. % of Door", description: "Artist receives the greater of the guarantee or their percentage of net ticket revenue.", fields: ["guarantee", "doorPercentage"] },
  { id: "plus", name: "Guarantee + % After Breakpoint", description: "Artist receives the guarantee plus a percentage of net ticket revenue after a breakpoint.", fields: ["guarantee", "doorPercentage", "breakpoint"] },
  { id: "flat", name: "Flat Guarantee", description: "Artist receives a fixed amount regardless of ticket sales.", fields: ["guarantee"] },
  { id: "door", name: "Straight Door Deal", description: "Artist receives a percentage of net ticket revenue with no guarantee.", fields: ["doorPercentage"] },
];

const getDefaultState = (venues) => {
  const firstVenue = venues[0] || {};
  return {
    venueId: firstVenue.id || null,
    expectedAttendance: firstVenue.avg_attendance || "",
    ticketPrice: firstVenue.avg_ticket_price || "",
    fbPerHead: firstVenue.default_fb_per_head || 15,
    addOnPerHead: firstVenue.avg_addon_per_head || 0,
    dealTypeId: "versus",
    guarantee: "",
    doorPercentage: 85,
    breakpoint: "",
    venueRoyaltyPct: 0,
  };
};

const CMCalculatorV2 = ({ venues = [], team, csrfToken, preloadOfferId }) => {
  const [state, setState] = useState(() => getDefaultState(venues));
  const [offers, setOffers] = useState([]);
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const [offerExpenses, setOfferExpenses] = useState(null);

  useEffect(() => {
    if (!team || !team.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.get(`/teams/${team.id}/cm/calculator/offers`)
      .then((res) => {
        const data = res.data || [];
        setOffers(data);
        if (preloadOfferId && data.length > 0) {
          const match = data.find((o) => o.id === preloadOfferId);
          if (match) handleLoadOffer(match.id, data);
        }
      })
      .catch(() => {});
  }, [team?.id]);

  const handleLoadOffer = (offerId, offersList) => {
    const list = offersList || offers;
    const offer = list.find((o) => o.id === parseInt(offerId));
    if (!offer) return;
    setSelectedOfferId(offer.id);
    setOfferExpenses(offer.expenses && offer.expenses.length > 0 ? offer.expenses : null);
    const changes = {
      dealTypeId: offer.deal_type || 'versus',
      guarantee: offer.guarantee || '',
      doorPercentage: offer.door_percentage || 50,
      ticketPrice: offer.ticket_price || '',
    };
    if (offer.ticket_forecast > 0) {
      changes.expectedAttendance = offer.ticket_forecast;
    }
    if (offer.venue_ownership_id) {
      const matchedVenue = venues.find((v) => v.id === offer.venue_ownership_id);
      if (matchedVenue) {
        changes.venueId = matchedVenue.id;
        changes.fbPerHead = matchedVenue.default_fb_per_head || 15;
      }
    }
    update(changes);
  };

  const venue = useMemo(
    () => venues.find((v) => v.id === state.venueId) || venues[0] || {},
    [state.venueId, venues]
  );

  const dealType = useMemo(
    () => DEAL_TYPES.find((d) => d.id === state.dealTypeId),
    [state.dealTypeId]
  );

  const cmTarget = useMemo(
    () => venue.shows_per_month > 0 ? Math.round((venue.monthly_overhead || 0) / venue.shows_per_month) : 0,
    [venue]
  );

  const mapOfferToOption = (o) => {
    const name = o.event_name || `Offer #${o.id}`;
    const artist = o.artist_name && o.artist_name !== name ? o.artist_name : null;
    return {
      value: o.id,
      label: artist ? `${name} — ${artist}` : name,
      sublabel: `${formatCurrency(o.guarantee)} | ${o.state}`,
      _offer: o,
    };
  };

  const defaultOfferOptions = useMemo(() => offers.map(mapOfferToOption), [offers]);

  const venueOptions = useMemo(
    () => venues.map((v) => ({
      value: v.id,
      label: v.name,
      sublabel: `Capacity: ${v.capacity}`,
    })),
    [venues]
  );

  const selectStyles = mergeSelectStyles({
    control: (base) => ({ ...base, minHeight: 36, fontSize: "0.85rem" }),
    menu: (base) => ({ ...base, zIndex: 10 }),
  });

  const formatOptionWithSublabel = (option, { context }) => (
    <div>
      <div style={{ fontSize: "0.85rem", fontWeight: 500 }}>{option.label}</div>
      {context === "menu" && option.sublabel && (
        <div style={{ fontSize: "0.75rem", color: "var(--od-c-717680, #717680)" }}>{option.sublabel}</div>
      )}
    </div>
  );

  const loadOfferOptions = useCallback((inputValue) => {
    if (!inputValue || inputValue.length < 2) return Promise.resolve(defaultOfferOptions);
    return axios.get(`/teams/${team.id}/cm/calculator/offers`, { params: { search: inputValue } })
      .then((res) => (res.data || []).map(mapOfferToOption))
      .catch(() => []);
  }, [team?.id, defaultOfferOptions]);

  const activeExpenses = offerExpenses || venue.expenses || [];

  const totalExpenses = useMemo(
    () => activeExpenses.reduce((sum, e) => sum + e.amount, 0),
    [activeExpenses]
  );

  const update = (changes) => {
    setState((prev) => ({ ...prev, ...changes }));
  };

  const handleVenueChange = (venueId) => {
    const newVenue = venues.find((v) => v.id === parseInt(venueId));
    if (!newVenue) return;
    setOfferExpenses(null);
    setSelectedOfferId(null);
    update({
      venueId: newVenue.id,
      expectedAttendance: newVenue.avg_attendance || "",
      ticketPrice: newVenue.avg_ticket_price || "",
      fbPerHead: newVenue.default_fb_per_head || 15,
      addOnPerHead: newVenue.avg_addon_per_head || 0,
    });
  };

  const calculate = (attendanceMultiplier = 1) => {
    const attendance = Math.round(
      (parseFloat(state.expectedAttendance) || 0) * attendanceMultiplier
    );
    const ticketPrice = parseFloat(state.ticketPrice) || 0;
    const fbPerHead = parseFloat(state.fbPerHead) || 0;
    const guarantee = parseFloat(state.guarantee) || 0;
    const doorPct = (parseFloat(state.doorPercentage) || 0) / 100;
    const breakpoint = parseFloat(state.breakpoint) || 0;

    const addOnPerHead = parseFloat(state.addOnPerHead) || 0;

    const ticketGross = attendance * ticketPrice;
    const addOnRevenue = attendance * addOnPerHead;
    const fbRevenue = attendance * fbPerHead;
    const totalRevenue = ticketGross + addOnRevenue + fbRevenue;
    const venueRoyaltyPct = (parseFloat(state.venueRoyaltyPct) || 0) / 100;
    const venueRoyalty = (ticketGross * venueRoyaltyPct);

    let artistPayout = 0;
    switch (state.dealTypeId) {
      case "versus":
        artistPayout = Math.max(guarantee, ticketGross * doorPct);
        break;
      case "plus":
        if (ticketGross > breakpoint) {
          artistPayout = guarantee + (ticketGross - breakpoint) * doorPct;
        } else {
          artistPayout = guarantee;
        }
        break;
      case "flat":
        artistPayout = guarantee;
        break;
      case "door":
        artistPayout = ticketGross * doorPct;
        break;
      default:
        artistPayout = guarantee;
    }

    const showProfit = totalRevenue - artistPayout - totalExpenses - venueRoyalty;

    return {
      attendance,
      ticketGross,
      addOnRevenue,
      fbRevenue,
      venueRoyalty,
      totalRevenue,
      artistPayout,
      totalExpenses,
      showProfit,
      cmTarget,
      cmDelta: showProfit - cmTarget,
      hitsCM: showProfit >= cmTarget,
    };
  };

  const baseAttendance = parseFloat(state.expectedAttendance) || 0;
  const selloutAttendance = venue.capacity || 0;
  const selloutMult = baseAttendance > 0 ? selloutAttendance / baseAttendance : 0;

  const scenarioDefs = [
    { mult: 0.5, label: "50% of Expected" },
    { mult: 0.75, label: "75% of Expected" },
    { mult: 1.0, label: "Expected", isExpected: true },
    { mult: selloutMult, label: "Sellout", isSellout: true },
  ];

  const scenarios = scenarioDefs.map(({ mult, label, isExpected, isSellout }) => ({
    label,
    multiplier: mult,
    isExpected: !!isExpected,
    isSellout: !!isSellout,
    ...calculate(mult),
  }));

  const expected = scenarios.find((s) => s.isExpected);
  const hasInputs =
    parseFloat(state.expectedAttendance) > 0 &&
    parseFloat(state.ticketPrice) > 0;

  const allInputsFilled =
    hasInputs &&
    dealType.fields.every((field) => {
      const val = parseFloat(state[field]);
      return val > 0;
    });

  return (
    <div>
      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between" style={{ marginBottom: 12, gap: 8 }}>
        <h4 style={{ fontWeight: 700, marginBottom: 0 }}>Deal Calculator</h4>
        <div className="d-flex flex-column flex-md-row" style={{ gap: 8 }}>
          <div style={{ minWidth: 280, flex: 1 }}>
            <AsyncSelect
              cacheOptions
              defaultOptions={defaultOfferOptions}
              loadOptions={loadOfferOptions}
              placeholder="Search offers..."
              value={defaultOfferOptions.find((o) => o.value === selectedOfferId) || null}
              onChange={(option) => {
                if (!option) return;
                if (option._offer) {
                  setOffers((prev) => {
                    const exists = prev.some((o) => o.id === option._offer.id);
                    return exists ? prev : [...prev, option._offer];
                  });
                }
                handleLoadOffer(option.value, option._offer ? [...offers, option._offer] : undefined);
              }}
              formatOptionLabel={formatOptionWithSublabel}
              noOptionsMessage={({ inputValue }) =>
                inputValue && inputValue.length < 2 ? "Type to search..." : "No offers found"
              }
              styles={selectStyles}
            />
          </div>
          <div style={{ minWidth: 200 }}>
            <Select
              isSearchable
              placeholder="Venue..."
              value={venueOptions.find((v) => v.value === state.venueId) || null}
              options={venueOptions}
              onChange={(option) => option && handleVenueChange(option.value)}
              formatOptionLabel={formatOptionWithSublabel}
              styles={selectStyles}
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-5">
          <div className="card" style={{ borderRadius: 8, marginBottom: 10, border: "1px solid var(--od-c-e9eaeb, #e9eaeb)" }}>
            <div className="card-body">
              <h6 style={{ fontWeight: 600 }} className="mb-3">Show Details</h6>

              <div className="row">
                <div className="col-6">
                  <div className="form-group">
                    <label className="small text-muted mb-1">Expected Attendance</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="e.g. 300"
                      value={state.expectedAttendance}
                      onChange={(e) => update({ expectedAttendance: e.target.value })}
                      style={{ backgroundColor: "var(--sl-bg-warning-primary)", fontWeight: 400, border: "1px solid var(--od-c-d5d7da, #d5d7da)" }}
                      max={venue.capacity}
                    />
                    <small className="text-muted">Max capacity: {venue.capacity}</small>
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group">
                    <label className="small text-muted mb-1">Ticket Price</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text" style={{ border: "1px solid var(--od-c-d5d7da, #d5d7da)" }}>$</span>
                      </div>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="e.g. 25"
                        value={state.ticketPrice}
                        onChange={(e) => update({ ticketPrice: e.target.value })}
                        style={{ backgroundColor: "var(--sl-bg-warning-primary)", fontWeight: 400, border: "1px solid var(--od-c-d5d7da, #d5d7da)" }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-6">
                  <div className="form-group">
                    <label className="small text-muted mb-1">F&B Per Head</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text" style={{ border: "1px solid var(--od-c-d5d7da, #d5d7da)" }}>$</span>
                      </div>
                      <input
                        type="number"
                        className="form-control"
                        value={state.fbPerHead}
                        onChange={(e) => update({ fbPerHead: e.target.value })}
                        style={{ backgroundColor: "var(--sl-bg-warning-primary)", fontWeight: 400, border: "1px solid var(--od-c-d5d7da, #d5d7da)" }}
                      />
                    </div>
                    <small className="text-muted">
                      Avg: {formatCurrency(venue.default_fb_per_head || 15)}/head
                    </small>
                  </div>
                </div>
                <div className="col-6">
                  <div className="form-group">
                    <label className="small text-muted mb-1">Add-On Per Head</label>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text" style={{ border: "1px solid var(--od-c-d5d7da, #d5d7da)" }}>$</span>
                      </div>
                      <input
                        type="number"
                        className="form-control"
                        value={state.addOnPerHead}
                        onChange={(e) => update({ addOnPerHead: e.target.value })}
                        style={{ backgroundColor: "var(--sl-bg-warning-primary)", fontWeight: 400, border: "1px solid var(--od-c-d5d7da, #d5d7da)" }}
                      />
                    </div>
                    <small className="text-muted">
                      Avg: {formatCurrency(venue.avg_addon_per_head || 0)}/head
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ borderRadius: 8, marginBottom: 10, border: "1px solid var(--od-c-e9eaeb, #e9eaeb)" }}>
            <div className="card-body">
              <h6 style={{ fontWeight: 600 }} className="mb-3">Deal Structure</h6>

              <Dropdown
                label="Deal Type"
                value={state.dealTypeId}
                options={DEAL_TYPES.map((d) => ({
                  value: d.id,
                  label: d.name,
                  sublabel: d.description,
                }))}
                onChange={(val) => update({ dealTypeId: val })}
              />

              <DealInputs dealType={dealType} state={state} update={update} />

              <div className="row" style={{ marginTop: 12 }}>
                <div className="col-6">
                  <div className="form-group">
                    <label className="small text-muted mb-1">Venue Royalty %</label>
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="0"
                        value={state.venueRoyaltyPct}
                        onChange={(e) => update({ venueRoyaltyPct: e.target.value })}
                        style={{ backgroundColor: "var(--sl-bg-warning-primary)", fontWeight: 400, border: "1px solid var(--od-c-d5d7da, #d5d7da)" }}
                        min={0}
                        max={100}
                      />
                      <div className="input-group-append">
                        <span className="input-group-text" style={{ border: "1px solid var(--od-c-d5d7da, #d5d7da)" }}>%</span>
                      </div>
                    </div>
                    <small className="text-muted">Applied to ticket gross</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card" style={{ borderRadius: 8, marginBottom: 10, border: "1px solid var(--od-c-e9eaeb, #e9eaeb)" }}>
            <div className="card-body" style={{ padding: "10px 14px" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span style={{ fontWeight: 600, fontSize: "0.8rem" }}>
                  Show Expenses
                  {offerExpenses && <span className="badge ml-2" style={{ background: 'var(--sl-bg-brand-primary)', color: 'var(--sl-text-brand)', fontSize: 10, fontWeight: 500 }}>From Offer</span>}
                  {!offerExpenses && activeExpenses.length > 0 && <span className="badge ml-2" style={{ background: 'var(--sl-bg-secondary)', color: 'var(--sl-text-secondary)', fontSize: 10, fontWeight: 500 }}>Venue Avg</span>}
                </span>
                <span className="text-muted" style={{ fontSize: "0.8rem" }}>{formatCurrency(totalExpenses)}</span>
              </div>
              {activeExpenses.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {activeExpenses.map((exp, i) => (
                    <div key={i} className="d-flex justify-content-between" style={{ fontSize: "0.75rem", color: "var(--od-c-717680, #717680)" }}>
                      <span>{exp.name}</span>
                      <span>{formatCurrency(exp.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <SummaryCard expected={expected} hasInputs={hasInputs} />
          <div style={{ marginTop: 12 }}>
            {hasInputs ? (
              <ScenarioTable scenarios={scenarios} venue={venue} cmTarget={cmTarget} />
            ) : (
              <div className="card" style={{ borderRadius: 8, border: "1px solid var(--od-c-e9eaeb, #e9eaeb)", minHeight: 400 }}>
                <div className="card-body d-flex align-items-center justify-content-center">
                  <div className="text-center text-muted">
                    <div style={{ fontSize: "2rem", opacity: 0.3 }}>&#9654;</div>
                    <p className="mt-2">Enter attendance and ticket price to see scenario analysis</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CMCalculatorV2;
